/* ═══════════════════════════════════════════════════════════════════════════
   WHAT CAN BE ORDERED, AND BY WHOM

   One list per restaurant, food and drink together, because a guest ordering
   at a table does not think in two lists. Every line carries an age limit,
   and that limit is the whole reason this file is not just a price list.

   ── Alkoholloven, which decides the shape and not the wording ─────────────

   Self-service does not discharge the licensee's duties. Under alkoholloven
   the place must judge **age** *and* **degree of intoxication** before it
   serves, and an app cannot do the second. So:

     · a line with `alc: 0` goes straight to the kitchen
     · a line with 18 or 20 goes to the waiter FIRST, whatever the app knows
       about the guest's age, and reaches the bar only once a person has
       approved it on the screen

   Verified age is what lets a guest *place* the order at all. It is not what
   lets the bar pour it. Collapsing those two into one check is the mistake
   that would make this feature unusable at a licensed premises, and it would
   look like a working feature right up until the first inspection.

   Limits are § 1-5: 18 for alcohol, 20 for spirits — including spirits inside
   a dessert. See data/menu.js on Coupe Colonel.
   ═══════════════════════════════════════════════════════════════════════ */

import { DISHES, DRINKS, MENUS, COURSES, DIETS, PRICE_ADJ } from '../data/menu.js';

export { COURSES, DIETS };

const round5 = n => Math.round(n / 5) * 5;

/** Which dishes this place serves. `all` is the shared card minus the pizzas —
    those belong to the wood oven, not to a brasserie. */
function dishesAt(businessId) {
  const m = MENUS[businessId];
  if (!m) return [];
  return m.all ? DISHES.filter(d => d.c !== 'pizza') : m.only.map(i => DISHES[i]);
}

/** Is there a menu to order from at all? */
export const hasMenu = businessId => !!MENUS[businessId];

/** The note above the card, in the restaurant's own words. */
export const menuIntro = businessId => MENUS[businessId]?.intro || '';

/**
 * Every orderable line at one place: food, then drink.
 *
 * `alc` is normalised here so no caller has to know that a dish states its
 * own limit while a drink inherits its group's — a caller that had to know
 * would eventually get one of them wrong.
 */
export function linesAt(businessId) {
  if (!MENUS[businessId]) return [];
  const adj = PRICE_ADJ[businessId] || 1;
  const out = [];

  dishesAt(businessId).forEach((d, i) => {
    out.push({
      id: 'd' + i,
      name: d.n,
      note: d.d,
      group: (COURSES.find(c => c[0] === d.c) || [, 'Meny'])[1],
      course: d.c,
      price: round5(d.p * adj),
      alc: d.alc || 0,
      allergens: d.a || [],
      diets: d.t || [],
      kind: 'food',
    });
  });

  DRINKS.forEach((g, gi) => g.rows.forEach((r, ri) => {
    out.push({
      id: `k${gi}_${ri}`,
      name: r.n,
      note: r.d,
      group: g.g,
      course: 'drikke',
      price: round5(r.p * adj),
      /* Spirits are twenty. The group says whether it is alcoholic at all;
         which kind it is comes from what the group is called, because an
         aperitif and an avec are both spirits and a glass of wine is not. */
      alc: g.alc ? (/aperitif|avec/i.test(g.g) ? 20 : 18) : 0,
      allergens: [],
      diets: [],
      kind: 'drink',
    });
  }));

  return out;
}

/** The groups in menu order, each with its lines. */
export function groupsAt(businessId) {
  const out = [];
  linesAt(businessId).forEach(l => {
    const had = out.find(g => g.name === l.group);
    if (had) had.lines.push(l);
    else out.push({ name: l.group, alc: l.alc, kind: l.kind, lines: [l] });
  });
  return out;
}

export const lineById = (businessId, id) => linesAt(businessId).find(l => l.id === id) || null;

/* ── Who may order what ───────────────────────────────────────────────── */

/** The highest age limit in a basket. One spirit in it makes the whole order
    a twenty. */
export const limitOf = (lines = []) => lines.reduce((m, l) => Math.max(m, l.alc || 0), 0);

/**
 * May this guest PLACE this order?
 *
 * `guest.age` is `{ over18, over20 }` as a verification answered it, or null
 * where nobody has verified anything. Not a date of birth: the platform
 * stores the answer and never the date — see domain/identity.js, and the
 * Vipps scope that returns `birthDate` without returning a national identity
 * number.
 *
 * Note what this does NOT decide: whether the bar may pour it. That is
 * `needsWaiter` below, and it is true for every alcoholic line regardless of
 * what this returns.
 */
export function mayOrder(guest, limit) {
  if (!limit) return true;
  if (!guest?.age) return false;
  return limit === 20 ? !!guest.age.over20 : !!guest.age.over18;
}

/**
 * Must a person look at this before it is poured?
 *
 * Every alcoholic line, always. The licensee has to judge intoxication and
 * an app cannot, so verified age gets the order as far as the screen and no
 * further. This is the line that makes the feature legal to run.
 */
export const needsWaiter = (lines = []) => limitOf(lines) > 0;

/** Why a line is locked, for the guest looking at it. */
export function lockWords(guest, limit) {
  if (!limit) return '';
  if (!guest?.age) {
    return `Over ${limit} only. Confirm your age with Vipps to order it here, `
         + 'or ask the waiter at the table.';
  }
  return limit === 20
    ? 'Spirits are over 20. Your confirmed age is under that, so the waiter takes this one.'
    : 'Over 18 only. Your confirmed age is under that, so the waiter takes this one.';
}

/** What a guest is told when an alcoholic order is placed. It is not on its
    way to the bar yet, and saying it is would be the app promising something
    the licence does not allow it to promise. */
export const waiterWords = () =>
  'A waiter has to approve anything with alcohol in it before the bar pours '
  + 'it — the law asks the place to judge more than age, and an app cannot. '
  + 'It is on their screen now.';
